import { enqueueTask, restartableTask } from 'ember-concurrency';
import { IResourceName } from 'volta/models/base-model';
import PoTask from 'volta/models/po-task';
import { ResourceName as SkuResourceName } from 'volta/models/sku';
import SkuTask from 'volta/models/sku-task';
import Task, {
  SkuTaskResourceName,
  TaskResourceName,
  TaskResourceNameFromParentRecordResourceName,
  taskToCommand,
  TTaskType,
  UTask
} from 'volta/models/task';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, inFilter } from 'volta/utils/filters-utils';

import Service, { inject as service } from '@ember/service';

import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type StoreService from './-store';

export default class TaskService extends Service {
  @service store!: StoreService;

  get isSaving() {
    return this.saveTask.isRunning;
  }

  // MAIN FETCH TASK
  fetchTasksTask = restartableTask(async (
    modelName: IResourceName,
    entityId: string,
    taskTypes: TTaskType[],
    include: string = 'createdBy,updatedBy,archivedBy,resolvedBy,assignees',
    sort: string = 'dueDate,resolvedAt'
  ) => {
    try {
      const tasksQuery: IJsonApiQuery = {
        page: {
          offset: 0,
          limit: 300
        },
        sort,
        include,
        filter: {
          taskType: inFilter(taskTypes)
        }
      };

      const response = await this.store.nestedQuery(
        modelName,
        entityId,
        TaskResourceName,
        tasksQuery
      );

      const SkuOrPoTaskResourceName = TaskResourceNameFromParentRecordResourceName(modelName);
      if (SkuOrPoTaskResourceName) {
        return normalize(SkuOrPoTaskResourceName.singular, response) as UTask[];
      }

      return normalize(TaskResourceName.singular, response) as Task[];
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  });

  // SPECIALIZED FETCHES
  fetchPlanningTasks = restartableTask(async (skuId: string) => {
    return this.fetchTasksTask.perform(SkuResourceName, skuId, ['PLANNING_TASK' as TTaskType]);
  });

  fetchExecutionTasks = restartableTask(async (skuId: string) => {
    return this.fetchTasksTask.perform(SkuResourceName, skuId, ['EXECUTION_TASK' as TTaskType]);
  });

  fetchCommentTasks = restartableTask(async (entityId: string, resourceName: IResourceName) => {
    return this.fetchTasksTask.perform(
      resourceName,
      entityId,
      ['COMMENT' as TTaskType],
      'createdBy',
      'createdAt'
    );
  });

  fetchSkuTasks = restartableTask(async (entityId: string, includeComments: boolean = false) => {
    const taskTypes: TTaskType[] = ['SKU_TASK'];
    if (includeComments) {
      taskTypes.push('COMMENT');
    }
    return this.fetchTasksTask.perform(SkuResourceName, entityId, taskTypes);
  });

  fetchSkusTasks = enqueueTask(async (entitiesId?: string[]) => {
    try {
      const tasksQuery: IJsonApiQuery = {
        page: {
          offset: 0,
          limit: 20
        },
        filter: {
          isResolved: eqFilter(false),
          taskType: inFilter(['SKU_TASK', 'PLANNING_TASK', 'EXECUTION_TASK'])
        },
        include: 'createdBy,updatedBy,archivedBy,resolvedBy,assignees',
        sort: 'dueDate'
      };

      if (entitiesId) {
        tasksQuery.filter = {
          linkedEntityId: inFilter(entitiesId)
        };
      }

      const response = await this.store.customQuery(
        SkuResourceName.singular,
        TaskResourceName.plural,
        undefined,
        tasksQuery
      );

      return normalize(SkuTaskResourceName.singular, response) as UTask[];
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  });

  fetchSkusComments = enqueueTask(async (entitiesId?: string[]) => {
    try {
      const tasksQuery: IJsonApiQuery = {
        page: {
          offset: 0,
          limit: 20
        },
        filter: { taskType: inFilter(['COMMENT']) },
        include: 'createdBy,updatedBy',
        sort: '-updatedAt'
      };

      if (entitiesId) {
        tasksQuery.filter = {
          linkedEntityId: inFilter(entitiesId)
        };
      }

      const response = await this.store.customQuery(
        SkuResourceName.singular,
        TaskResourceName.plural,
        undefined,
        tasksQuery
      );

      return normalize(SkuTaskResourceName.singular, response) as UTask[];
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  });

  // CRUD METHODS
  saveTask = enqueueTask(async (task: UTask) => {
    try {
      if (task.get('hasDirtyAttributes')) {
        const payload = taskToCommand(task);
        const TaskModel = TaskService.TaskModel(task);

        if (TaskModel) {
          if (!task.id) {
            return (await TaskModel.createTask(payload)) as UTask;
          } else {
            return (await TaskModel.updateTask(payload)) as UTask;
          }
        }
      }

      return task;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  });

  resolveTask = enqueueTask(async (task: UTask) => {
    return this.onResolve.perform(task, false);
  });

  reopenTask = enqueueTask(async (task: UTask) => {
    return this.onResolve.perform(task, true);
  });

  onResolve = enqueueTask(async (task: UTask, isResolved: boolean) => {
    try {
      if (task.get('hasDirtyAttributes')) {
        const payload = {
          taskId: task.id,
          linkedEntityId: task.linkedEntityId
        };

        if (task instanceof SkuTask) {
          return isResolved ? await SkuTask.reopenTask(payload) as UTask : await SkuTask.resolveTask(payload) as UTask;
        }
      }
      return task;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  });

  deleteTask = enqueueTask(async (task: UTask) => {
    try {
      const payload = taskToCommand(task);
      const TaskModel = TaskService.TaskModel(task);

      if (task.id) {
        if (TaskModel) {
          const savedTask = await TaskModel.deleteTask(payload) as UTask;
          task.deleteRecord();
          return savedTask;
        }
      } else {
        task.deleteRecord();
      }
      return task;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  });

  private static TaskModel(task: UTask) {
    let TaskModel: typeof PoTask | typeof SkuTask | undefined;
    if (task instanceof SkuTask) {
      TaskModel = SkuTask;
    } else {
      TaskModel = PoTask;
    }
    return TaskModel;
  }
}

declare module '@ember/service' {
  interface Registry {
    'task-service': TaskService;
  }
}
